import fr from 'facet-rest';
import React, {
  useState,
} from 'react';
import useLink
  from 'Hooks/useLink';
import PropTypes
  from 'prop-types';
import DataContainer
  from 'Components/UI/DataContainer';
import {
  FDSecondaryActionButton,
  SwitchInput,
} from '@facet-wealth/design';
import {
  useSnackbarContext,
} from 'Context/SnackbarContext';

const UserStatus = ({
  resource,
  refreshCollection,
}) => {
  const { displaySuccess, displayErrorMessage } = useSnackbarContext();

  const [isSwitchDisabled, setIsSwitchDisabled] = useState(false);

  return (
    <SwitchInput
      disabled={isSwitchDisabled}
      label={resource.attr('label')}
      onChange={() => {
        setIsSwitchDisabled(true);
        const newVal = resource.attr('isUserActive') !== 'true';
        resource.attr('isUserActive', newVal.toString());
        resource.submit()
          .then(() => {
            displaySuccess('Member\'s user status updated');
          })
          .catch((err) => {
            displayErrorMessage(err, 'Failed to updated member\'s user status');
          })
          .finally(() => {
            setIsSwitchDisabled(false);
            refreshCollection();
          });
      }}
      value={resource.attr('isUserActive') === 'true'}
    />
  );
};

UserStatus.propTypes = {
  resource: PropTypes.object.isRequired,
  refreshCollection: PropTypes.func.isRequired,
};

const Foo = ({
  collection,
  refreshCollection,
}) => {
  const [primaryEmailButtonDisabled, setPrimaryEmailButtonDisabled] = useState(false);
  const [primaryUnlockButtonDisabled, setPrimaryUnlockButtonDisabled] = useState(false);
  const [spouseEmailButtonDisabled, setSpouseEmailButtonDisabled] = useState(false);
  const [spouseUnlockButtonDisabled, setSpouseUnlockButtonDisabled] = useState(false);

  const { displaySuccess, displayErrorMessage } = useSnackbarContext();
  const resource = collection?.resources()?.head();
  if (!collection) {
    return null;
  }

  return (
    <div>
      <p>{resource.attr('description')}</p>
      <div>
        <div><b>{resource.attr('primaryMemberLabel')}</b></div>
        <div><b>Enabled multi-factors:</b> {resource.attr('enabledMfaForPrimaryMember')}</div>
        {
          resource.withRelationship(
            fr.relationships.primaryClientMfaReset.name,
            (rel) => {
              const rio = rel.attr('data');
              const primaryClient = collection.included().list().find((r) => r.id() === rio.id && r.type() === rio.type);
              return (
                <FDSecondaryActionButton
                  size={'sm'}
                  disabled={primaryEmailButtonDisabled || rel.meta('disabled')}
                  icon={undefined}
                  label={rel.meta('prompt')}
                  onClick={
                    () => {
                      setPrimaryEmailButtonDisabled(true);
                      primaryClient.link('self').post()
                        .then(() => displaySuccess('Reset email sent'))
                        .catch((err) => displayErrorMessage(err, 'Failed to send reset email'))
                        .finally(() => setPrimaryEmailButtonDisabled(false));
                    }
                  }
                />
              );
            },
            () => null,
          )
        }
        {
          resource.withRelationship(
            fr.relationships.primaryClientIdentityVerificationUnlock.name,
            (rel) => {
              const rio = rel.attr('data');
              const primaryClient = collection.included().list().find((r) => r.id() === rio.id && r.type() === rio.type);
              return (
                <FDSecondaryActionButton
                  size={'sm'}
                  disabled={primaryUnlockButtonDisabled || rel.meta('disabled')}
                  icon={undefined}
                  label={rel.meta('prompt')}
                  onClick={
                    () => {
                      setPrimaryUnlockButtonDisabled(true);
                      primaryClient.link('self').post()
                        .then(() => {
                          displaySuccess('Member unlocked');
                          refreshCollection();
                        })
                        .catch((err) => {
                          displayErrorMessage(err, 'Failed to unlock member');
                          setPrimaryUnlockButtonDisabled(false);
                        });
                    }
                  }
                />
              );
            },
            () => null,
          )
        }
        {
          resource.withRelationship(
            fr.relationships.primaryUserStatus.name,
            (rel) => {
              const rio = rel.attr('data');
              const userStatusResource = collection.included().list().find((r) => rio.id === r.id() && rio.type === r.type());
              if (!userStatusResource) {
                return null;
              }
              return (
                <UserStatus resource={userStatusResource} refreshCollection={refreshCollection} />
              );
            },
            () => null,
          )
        }
      </div>
      <br />
      <div>
        {
          resource.withRelationship(
            fr.relationships.secondaryClient.name,
            (_) => {
              return (<>
                <div><b>{resource.attr('spouseMemberLabel')}</b></div>
                <div><b>Enabled multi-factors:</b> {resource.attr('enabledMfaForSpouseMember')}</div>
              </>);
            },
            () => null,
          )
        }
        {
          resource.withRelationship(
            fr.relationships.secondaryClientMfaReset.name,
            (rel) => {
              const rio = rel.attr('data');
              const secondaryClient = collection.included().list().find((r) => r.id() === rio.id && r.type() === rio.type);
              return (
                <FDSecondaryActionButton
                  size={'sm'}
                  disabled={spouseEmailButtonDisabled || rel.meta('disabled')}
                  icon={undefined}
                  label={rel.meta('prompt')}
                  onClick={
                    () => {
                      setSpouseEmailButtonDisabled(true);
                      secondaryClient.link('self').post()
                        .then(() => displaySuccess('Reset email sent'))
                        .catch((err) => displayErrorMessage(err, 'Failed to send reset email'))
                        .finally(() => setSpouseEmailButtonDisabled(false));
                    }
                  }
                />
              );
            },
            () => null,
          )
        }
        {
          resource.withRelationship(
            fr.relationships.secondaryClientIdentityVerificationUnlock.name,
            (rel) => {
              const rio = rel.attr('data');
              const secondaryClient = collection.included().list().find((r) => r.id() === rio.id && r.type() === rio.type);
              return (
                <FDSecondaryActionButton
                  size={'sm'}
                  disabled={spouseUnlockButtonDisabled || rel.meta('disabled')}
                  icon={undefined}
                  label={rel.meta('prompt')}
                  onClick={
                    () => {
                      setSpouseUnlockButtonDisabled(true);
                      secondaryClient.link('self').post()
                        .then(() => {
                          displaySuccess('Member unlocked');
                          refreshCollection();
                        })
                        .catch((err) => {
                          displayErrorMessage(err, 'Failed to unlock member');
                          setSpouseUnlockButtonDisabled(false);
                        });
                    }
                  }
                />
              );
            },
            () => null,
          )
        }
        {
          resource.withRelationship(
            fr.relationships.secondaryUserStatus.name,
            (rel) => {
              const rio = rel.attr('data');
              const userStatusResource = collection.included().list().find((r) => rio.id === r.id() && rio.type === r.type());
              if (!userStatusResource) {
                return null;
              }
              return (
                <UserStatus resource={userStatusResource} refreshCollection={refreshCollection} />
              );
            },
            () => null,
          )
        }
      </div>
    </div>
  );
};

Foo.propTypes = {
  collection: PropTypes.object.isRequired,
  refreshCollection: PropTypes.func.isRequired,
};

const SecuritySection = ({
  relationshipLink,
}) => {
  const {
    isInitializing,
    loadingError,
    collection,
    refreshCollection,
  } = useLink(relationshipLink);

  return (
    <DataContainer
      component={Foo}
      loading={isInitializing}
      loadingError={loadingError}
      componentProps={{
        collection,
        refreshCollection,
      }}
    />
  );
};

SecuritySection.propTypes = {
  relationshipLink: PropTypes.object.isRequired,
};

export default SecuritySection;
